import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, Facebook, Instagram, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { getTeamCategories } from "@/lib/api";
import NewsletterForm from "@/components/NewsletterForm";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kickoff-central-10/artifacts/72zh29ie_Middel%2020%40300x.png";

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Left navigation (before logo)
const navLinksLeft = [
  { label: "Home", path: "/" },
  { label: "Nieuws", path: "/news" },
];

// Right navigation (after logo) - Shop & Vacatures now in top bar
const navLinksRight = [];

// Club Info sub-items
const clubInfoItems = [
  { label: "Missie & Visie", path: "/club/missie-visie" },
  { label: "Geschiedenis", path: "/club/geschiedenis" },
  { label: "Organigram", path: "/club/organigram" },
  { label: "Logo Downloads", path: "/club/logo" },
];

// Sport Eldorado sub-items
const sportEldoradoItems = [
  { label: "Over Ons", path: "/sport-eldorado" },
  { label: "Missie & Visie", path: "/sport-eldorado/missie-visie" },
  { label: "Team", path: "/sport-eldorado/team" },
  { label: "Galerij", path: "/sport-eldorado/galerij" },
  { label: "Kalender", path: "/sport-eldorado/kalender" },
];

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const [teamCategories, setTeamCategories] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "light") {
      setIsDark(false);
      document.documentElement.classList.add("light-theme");
    }
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getTeamCategories();
        setTeamCategories(res.data.filter(c => c.is_active));
      } catch (e) {
        console.error("Error fetching team categories:", e);
      }
    };
    fetchCategories();
  }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
    if (isDark) {
      document.documentElement.classList.add("light-theme");
      localStorage.setItem("theme", "light");
    } else {
      document.documentElement.classList.remove("light-theme");
      localStorage.setItem("theme", "dark");
    }
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[hsl(224,71%,4%)]' : 'bg-slate-100'}`}>
      {/* Sticky wrapper for mobile */}
      <div className="sticky top-0 z-50 lg:static">
      {/* Top Bar */}
      <div className={`${isDark ? 'bg-slate-950 border-b border-white/10' : 'bg-slate-900 border-b border-slate-800'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-10">
            {/* Left - Social Links */}
            <div className="hidden lg:flex items-center gap-3">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[hsl(217,91%,60%)]">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[hsl(217,91%,60%)]">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[hsl(217,91%,60%)]">
                <TikTokIcon className="h-4 w-4" />
              </a>
            </div>

            {/* Right - Shop & Vacatures + Theme Toggle */}
            <div className="flex items-center gap-4 ml-auto">
              <Link 
                to="/inschrijvingen" 
                className="text-sm px-3 py-1 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-full transition-colors"
              >
                Inschrijven
              </Link>
              <Link to="/shop" className="text-sm text-slate-300 hover:text-white transition-colors">
                Shop
              </Link>
              
              <Link to="/vacatures" className="text-sm text-slate-300 hover:text-white transition-colors">
                Vacatures
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-8 w-8 text-slate-300 hover:text-white hover:bg-white/10"
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`relative ${isDark ? 'bg-slate-900/95 border-b border-white/10' : 'bg-white border-b border-slate-200 shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Left Navigation */}
            <nav className="hidden lg:flex items-center gap-6 flex-1">
              {navLinksLeft.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm font-medium transition-colors ${
                    isActive(link.path) 
                      ? "text-[hsl(48,96%,53%)]" 
                      : isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-slate-900"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Teams Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`nav-link flex items-center gap-1 text-sm font-medium transition-colors ${
                      isActive("/teams") || isActive("/games") || isActive("/ranking")
                        ? "text-[hsl(48,96%,53%)]" 
                        : isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-slate-900"
                    }`}
                  >
                    Teams <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className={isDark ? "bg-slate-900 border-white/10" : "bg-white border-slate-200 shadow-lg"}>
                  <DropdownMenuItem asChild>
                    <Link to="/games" className={isDark ? "text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100 cursor-pointer"}>
                      Wedstrijden
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/ranking" className={isDark ? "text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100 cursor-pointer"}>
                      Ranglijst
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className={isDark ? "bg-white/10" : "bg-slate-200"} />
                  {teamCategories.map((team) => (
                    <DropdownMenuItem key={team.id} asChild>
                      <Link to={`/teams/${team.code}`} className={isDark ? "text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100 cursor-pointer"}>
                        {team.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Club Info Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`nav-link flex items-center gap-1 text-sm font-medium transition-colors ${
                      isActive("/club") ? "text-[hsl(48,96%,53%)]" : isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-slate-900"
                    }`}
                  >
                    Club Info <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className={isDark ? "bg-slate-900 border-white/10" : "bg-white border-slate-200 shadow-lg"}>
                  {clubInfoItems.map((item) => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link to={item.path} className={isDark ? "text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100 cursor-pointer"}>
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Center Logo - Larger with overlay extending down */}
            <Link to="/" className="flex-shrink-0 mx-6 relative z-10">
              <img 
                src={LOGO_URL} 
                alt="Futsal Gent" 
                className="h-24 md:h-28 w-auto drop-shadow-xl translate-y-3"
              />
            </Link>

            {/* Right Navigation */}
            <nav className="hidden lg:flex items-center gap-6 flex-1 justify-end">
              {/* Sport Eldorado Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className={`nav-link flex items-center gap-1 text-sm font-medium transition-colors ${
                      isActive("/sport-eldorado") ? "text-[hsl(48,96%,53%)]" : isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-slate-900"
                    }`}
                  >
                    Sport Eldorado <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className={isDark ? "bg-slate-900 border-white/10" : "bg-white border-slate-200 shadow-lg"}>
                  {sportEldoradoItems.map((item) => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link to={item.path} className={isDark ? "text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100 cursor-pointer"}>
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/sponsors" className={`nav-link text-sm font-medium transition-colors ${isActive("/sponsors") ? "text-[hsl(48,96%,53%)]" : isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-slate-900"}`}>
                Sponsors
              </Link>
              
              <Link to="/contact" className={`nav-link text-sm font-medium transition-colors ${isActive("/contact") ? "text-[hsl(48,96%,53%)]" : isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-slate-900"}`}>
                Contact
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} className="lg:hidden">
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className={`lg:hidden ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className={`${isDark ? 'bg-slate-950 border-white/10' : 'bg-white border-slate-200'} w-[300px] !p-0 flex flex-col`}>
                <div className="flex flex-col gap-6 mt-8 overflow-y-auto px-6 pb-6 flex-1">
                  {/* Theme Toggle - Mobile */}
                  <Button
                    variant="outline"
                    onClick={toggleTheme}
                    className={`justify-start ${isDark ? 'border-white/20 text-white' : 'border-slate-300 text-slate-900'}`}
                  >
                    {isDark ? <Sun className="h-5 w-5 mr-2" /> : <Moon className="h-5 w-5 mr-2" />}
                    {isDark ? "Lichte Modus" : "Donkere Modus"}
                  </Button>

                  {/* Main Nav Links */}
                  {[...navLinksLeft, ...navLinksRight].map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-lg font-medium transition-colors ${
                        isActive(link.path) 
                          ? "text-[hsl(48,96%,53%)]" 
                          : isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-slate-900"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  
                  {/* Teams Section - Mobile */}
                  <div className={`border-t ${isDark ? 'border-white/10' : 'border-slate-200'} pt-4`}>
                    <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'} mb-3`}>Teams & Competitie</p>
                    <Link to="/games" onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-700 hover:text-slate-900'}`}>
                      Wedstrijden
                    </Link>
                    <Link to="/ranking" onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-700 hover:text-slate-900'}`}>
                      Ranglijst
                    </Link>
                    {teamCategories.map((team) => (
                      <Link key={team.id} to={`/teams/${team.code}`} onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-700 hover:text-slate-900'}`}>
                        {team.name}
                      </Link>
                    ))}
                  </div>

                  {/* Club Info Section - Mobile */}
                  <div className={`border-t ${isDark ? 'border-white/10' : 'border-slate-200'} pt-4`}>
                    <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'} mb-3`}>Club Info</p>
                    {clubInfoItems.map((item) => (
                      <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-700 hover:text-slate-900'}`}>
                        {item.label}
                      </Link>
                    ))}
                  </div>

                  {/* Sport Eldorado Section - Mobile */}
                  <div className={`border-t ${isDark ? 'border-white/10' : 'border-slate-200'} pt-4`}>
                    <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'} mb-3`}>Sport Eldorado</p>
                    {sportEldoradoItems.map((item) => (
                      <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)} className={`block py-2 ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-700 hover:text-slate-900'}`}>
                        {item.label}
                      </Link>
                    ))}
                  </div>

                  <div className={`flex gap-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                    <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className={isDark ? "text-slate-400 hover:text-[hsl(217,91%,60%)]" : "text-slate-500 hover:text-blue-600"}>
                      <Facebook className="h-6 w-6" />
                    </a>
                    <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className={isDark ? "text-slate-400 hover:text-[hsl(217,91%,60%)]" : "text-slate-500 hover:text-pink-600"}>
                      <Instagram className="h-6 w-6" />
                    </a>
                    <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className={isDark ? "text-slate-400 hover:text-[hsl(217,91%,60%)]" : "text-slate-500 hover:text-slate-900"}>
                      <TikTokIcon className="h-6 w-6" />
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      </div>

      {/* Main Content */}
      <main>
        <Outlet context={{ isDark }} />
      </main>

      {/* Footer */}
      <footer className={`${isDark ? 'bg-slate-950 border-t border-white/10' : 'bg-slate-900 border-t border-slate-800'} mt-20`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="Futsal Gent" className="h-16 w-auto" />
                <span className="font-bold text-xl text-white font-['Chivo']">FUTSAL GENT</span>
              </div>
              <p className="text-slate-400 text-sm max-w-md">
                Sinds 2016 is Futsal Gent toegewijd aan excellentie in futsal. Sluit je aan bij ons op weg naar de top van het Belgische futsal.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold text-white mb-4">Snelle Links</h4>
              <div className="flex flex-col gap-2">
                <Link to="/" className="text-slate-400 hover:text-white text-sm transition-colors">Home</Link>
                <Link to="/news" className="text-slate-400 hover:text-white text-sm transition-colors">Nieuws</Link>
                <Link to="/games" className="text-slate-400 hover:text-white text-sm transition-colors">Wedstrijden</Link>
                <Link to="/ranking" className="text-slate-400 hover:text-white text-sm transition-colors">Ranglijst</Link>
                <Link to="/vacatures" className="text-slate-400 hover:text-white text-sm transition-colors">Vacatures</Link>
                <Link to="/shop" className="text-slate-400 hover:text-white text-sm transition-colors">Shop</Link>
                <Link to="/sport-eldorado" className="text-slate-400 hover:text-white text-sm transition-colors">Sport Eldorado</Link>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-bold text-white mb-4">Contact</h4>
              <div className="text-slate-400 text-sm space-y-2">
                <p>Sporthal Hekers</p>
                <p>Ter Linden 29</p>
                <p>9052 Gent, België</p>
                <p className="pt-2">info@futsalgent.be</p>
              </div>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="font-bold text-white mb-4">Nieuwsbrief</h4>
              <p className="text-slate-400 text-sm mb-3">
                Blijf op de hoogte van het laatste nieuws
              </p>
              <NewsletterForm isDark={isDark} />
            </div>
          </div>

          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Futsal Gent. Alle rechten voorbehouden.
            </p>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-slate-500 hover:text-slate-400 text-sm transition-colors">
                Privacybeleid
              </Link>
              <Link to="/admin/login" className="text-slate-500 hover:text-slate-400 text-sm transition-colors">
                Admin Login
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
